import { useMerchant } from "@/hooks/useMerchant";
import { useTRPC } from "@/trpc/client";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30000;

export function useDownloadPayoutReceipt() {
  const trpc = useTRPC();
  const { merchant } = useMerchant();
  const queryClient = useQueryClient();
  const generatePdf = useMutation(trpc.payouts.generatePdf.mutationOptions());

  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(
    () => new Set()
  );
  // Synchronous mirror of downloadingIds used from the click handler so the
  // re-entry guard doesn't rely on the stale `useState` snapshot captured
  // in the useCallback closure.
  const inFlightRef = useRef<Set<string>>(new Set());
  const pollingRefs = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const addDownloading = (id: string) => {
    inFlightRef.current.add(id);
    setDownloadingIds((prev) => new Set(prev).add(id));
  };

  const removeDownloading = (id: string) => {
    inFlightRef.current.delete(id);
    setDownloadingIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const stopPolling = (id: string) => {
    const interval = pollingRefs.current.get(id);
    if (interval) {
      clearInterval(interval);
      pollingRefs.current.delete(id);
    }
  };

  useEffect(() => {
    return () => {
      pollingRefs.current.forEach((interval) => clearInterval(interval));
      pollingRefs.current.clear();
    };
  }, []);

  const fetchUrl = (payoutId: string) =>
    queryClient.fetchQuery(
      trpc.payouts.getPdf.queryOptions({
        rentalsMerchantId: merchant!.id,
        id: payoutId,
      })
    );

  // The presigned URL is signed with `Content-Disposition: attachment`, so
  // clicking an anchor pointing at it makes the browser start a download
  // without navigating away. Using a transient anchor (instead of
  // `window.location.href`) keeps the mechanism parallel-safe: each call
  // owns its own DOM node, so two downloads kicked off at nearly the same
  // time don't stomp on each other via shared global state. Works from any
  // context (click handler, timer callback) without needing transient user
  // activation because the attachment response is treated as a download
  // rather than a programmatic navigation.
  const triggerDownload = (url: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.rel = "noopener";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const startPolling = (payoutId: string) => {
    const startTime = Date.now();
    const interval = setInterval(async () => {
      try {
        if (Date.now() - startTime > POLL_TIMEOUT_MS) {
          stopPolling(payoutId);
          removeDownloading(payoutId);
          return;
        }

        const url = await fetchUrl(payoutId);
        if (!url) return;

        stopPolling(payoutId);
        removeDownloading(payoutId);
        queryClient.invalidateQueries({
          queryKey: trpc.payouts.list.queryKey(),
        });
        triggerDownload(url);
      } catch {
        stopPolling(payoutId);
        removeDownloading(payoutId);
      }
    }, POLL_INTERVAL_MS);
    pollingRefs.current.set(payoutId, interval);
  };

  const downloadReceipt = useCallback(
    async (payoutId: string, receiptGenerated: boolean) => {
      if (!merchant?.id || inFlightRef.current.has(payoutId)) return;
      addDownloading(payoutId);

      try {
        if (receiptGenerated) {
          const url = await fetchUrl(payoutId);
          if (url) {
            removeDownloading(payoutId);
            triggerDownload(url);
            return;
          }
          // receiptGenerated was true but the file is missing from S3
          // (e.g. was deleted). Fall through to regenerate and poll.
        }

        await generatePdf.mutateAsync({
          rentalsMerchantId: merchant.id,
          id: payoutId,
        });
        startPolling(payoutId);
      } catch {
        stopPolling(payoutId);
        removeDownloading(payoutId);
      }
    },
    [merchant?.id]
  );

  return { downloadReceipt, downloadingIds };
}
